/**
 * Formulário de localização estilo WhatsApp (bottom sheet + tokens do DS).
 */
import React, { useEffect, useRef, useState } from 'react';
import Modal from '../../../../components/ui/Modal';
import SearchInput from '../../../../components/ui/SearchInput';
import { Button } from '../../../../components/ui/Button';
import { AppIcon } from '../../../../components/icons/AppIcon';
import { useLocationPlaceSearch } from '../../hooks/useLocationPlaceSearch';
import {
    type PlaceResult,
    cleanPlaceAddress,
    isCoordinateOnlyText,
} from '../../lib/locationPlaces';
import { hasGooglePlacesSearch } from '../../lib/googlePlacesSearch';
import { notify } from '../../../../utils/toast';

const ROW_BTN =
    'flex min-h-14 w-full gap-3 px-4 py-3 text-left transition-colors hover:bg-surface-subtle focus-visible:bg-surface-subtle active:bg-surface-subtle disabled:opacity-60 sm:px-5';
const ICON_WRAP =
    'flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-surface-subtle text-secondary';

export interface ChatLocationFormInput {
    latitude: number;
    longitude: number;
    name?: string;
    address?: string;
}

interface ChatLocationFormProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (input: ChatLocationFormInput) => Promise<void>;
}

const LocationGlyph = ({ size, className = '' }: { size: 'sm' | 'md'; className?: string }) => (
    <span className={`${ICON_WRAP} ${className}`.trim()}>
        <AppIcon name="location" className={size === 'md' ? 'h-5 w-5' : 'h-4 w-4'} aria-hidden />
    </span>
);

export const ChatLocationForm: React.FC<ChatLocationFormProps> = ({ isOpen, onClose, onSubmit }) => {
    const {
        query, setQuery, results, isSearching, isSearchMode,
        isLocating, currentPlace, locateMe, reset,
    } = useLocationPlaceSearch(isOpen);
    const [pendingSendCurrent, setPendingSendCurrent] = useState(false);
    const sentRef = useRef(false);

    useEffect(() => {
        if (!isOpen) {
            setPendingSendCurrent(false);
            sentRef.current = false;
        }
    }, [isOpen]);

    const handleClose = () => {
        setPendingSendCurrent(false);
        reset();
        onClose();
    };

    const sendPlace = (place: PlaceResult) => {
        if (sentRef.current) return;
        if (!Number.isFinite(place.latitude) || !Number.isFinite(place.longitude)) {
            notify.error('Este local não tem coordenadas válidas.');
            return;
        }
        const address = cleanPlaceAddress(place);
        if (!address || isCoordinateOnlyText(address)) {
            notify.error('Endereço ainda não disponível. Aguarde ou tente de novo.');
            return;
        }
        sentRef.current = true;
        onClose();
        void onSubmit({
            latitude: place.latitude,
            longitude: place.longitude,
            name: place.name || 'Localização',
            address,
        }).catch(() => {
            /* erro já notificado */
        });
    };

    // GPS ainda não tinha voltado no clique. Envia o local já resolvido, sem novo geocode.
    useEffect(() => {
        if (!isOpen || !pendingSendCurrent || isLocating || !currentPlace) return;
        setPendingSendCurrent(false);
        sendPlace(currentPlace);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- dispara quando o GPS libera o local atual
    }, [isOpen, pendingSendCurrent, isLocating, currentPlace]);

    const handleSendCurrent = () => {
        if (pendingSendCurrent) return;
        if (currentPlace && !isLocating) {
            sendPlace(currentPlace);
            return;
        }
        setPendingSendCurrent(true);
        if (!isLocating) locateMe();
    };

    const listHeading = isSearching
        ? (isSearchMode ? 'Buscando…' : 'Carregando próximos…')
        : (isSearchMode ? 'Resultados' : 'Locais próximos');

    const emptyMessage = isSearchMode
        ? (hasGooglePlacesSearch()
            ? 'Nenhuma empresa encontrada. Tente o nome da loja (cidade opcional).'
            : 'Não achamos essa empresa no mapa aberto. Configure VITE_GOOGLE_MAPS_API_KEY para busca completa.')
        : (isLocating ? 'Aguardando sua localização…' : 'Nenhum local próximo encontrado. Busque pelo nome.');

    const currentTitle = pendingSendCurrent
        ? 'Enviando sua localização…'
        : isLocating
          ? 'Obtendo sua localização…'
          : 'Enviar localização atual';

    const currentSubtitle = isLocating
        ? 'Obtendo GPS e endereço…'
        : !currentPlace
          ? 'Usa GPS e endereços próximos'
          : (currentPlace.address || currentPlace.name);

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            size="md"
            overlayClassName="items-end p-0 sm:items-center sm:p-4"
            className="max-h-[min(92dvh,36rem)] w-full rounded-t-2xl rounded-b-none sm:max-h-[min(90vh,36rem)] sm:rounded-2xl"
        >
            <div className="flex justify-center pb-1 pt-3 sm:hidden" aria-hidden="true">
                <span className="h-1 w-10 rounded-full bg-border-strong" />
            </div>

            <Modal.Header onClose={handleClose} className="px-4 py-3 sm:px-5 sm:py-4">
                <span className="text-body-md font-semibold text-default sm:text-heading-sm">
                    Localização
                </span>
            </Modal.Header>

            <search className="border-b border-subtle px-4 py-3 sm:px-5">
                <SearchInput
                    id="location-search"
                    value={query}
                    onChange={setQuery}
                    placeholder="Buscar loja, empresa ou endereço…"
                    className="[&_.input]:h-11 [&_.input]:min-h-11"
                />
            </search>

            <Modal.Body noPadding className="flex min-h-0 flex-1 flex-col space-y-0">
                <div className="max-h-[min(55dvh,22rem)] overflow-y-auto overscroll-contain scrollbar-premium sm:max-h-[min(55vh,22rem)]">
                    <button
                        type="button"
                        onClick={handleSendCurrent}
                        disabled={pendingSendCurrent}
                        className={`${ROW_BTN} items-center border-b border-subtle`}
                    >
                        <LocationGlyph size="md" />
                        <span className="min-w-0 flex-1">
                            <span className="block text-body-sm font-semibold text-default">{currentTitle}</span>
                            <span className="mt-0.5 block truncate text-caption text-muted">{currentSubtitle}</span>
                        </span>
                    </button>

                    <section aria-labelledby="location-list-heading" className="pb-[env(safe-area-inset-bottom)] sm:pb-0">
                        <h4 id="location-list-heading" className="text-label px-4 py-2.5 sm:px-5">
                            {listHeading}
                        </h4>

                        {results.length > 0 ? (
                            <ul className="list-none p-0">
                                {results.map((place) => (
                                    <li key={place.id} className="border-b border-subtle last:border-b-0">
                                        <button
                                            type="button"
                                            disabled={pendingSendCurrent}
                                            onClick={() => void sendPlace(place)}
                                            className={`${ROW_BTN} items-start`}
                                        >
                                            <LocationGlyph size="sm" className="mt-0.5" />
                                            <span className="min-w-0 flex-1">
                                                <span className="block truncate text-body-sm font-medium text-default">
                                                    {place.name}
                                                </span>
                                                <span className="mt-0.5 block line-clamp-2 text-caption leading-snug text-muted">
                                                    {place.address}
                                                </span>
                                            </span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            !isSearching && (
                                <p className="px-4 py-8 text-center text-body-sm text-muted sm:px-5">
                                    {emptyMessage}
                                </p>
                            )
                        )}
                    </section>
                </div>
            </Modal.Body>

            <Modal.Footer className="justify-stretch px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:justify-end sm:px-5 sm:py-4 sm:pb-4 [&_button]:min-h-11 [&_button]:w-full sm:[&_button]:w-auto">
                <Button
                    type="button"
                    variant="outline"
                    size="lg"
                    onClick={handleClose}
                    className="rounded-xl"
                >
                    Cancelar
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
